/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ReportTemplatesPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import { Box } from "@mui/material";
import TemplateLayout from "../Templates/TemplateLayout";
import TemplateManager from "../Templates/TemplateManager";
import reportTemplateAction from "../../../redux/actions/reporting/reportTemplateActions";
import FileUpload from "../../../components/file-upload/FileUpload";
import mammoth from "mammoth";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../redux/actions/actionHandler";

function ReportTemplatesPage() {
  const [uploadOpen, setUploadOpen] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [templateName] = useState("");

  const REPORT_SCOPE = "VEHICLE_PROGRAM"; // or from dropdown

  const dispatch = useDispatch();

  const getTemplateNameFromFile = (file) => file.name.replace(/\.[^/.]+$/, "");

  const validateReportTemplate = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });

    const regex = /\[\[(.*?)\]\]/g; //  Updated regex

    const tags = [];
    let match;

    while ((match = regex.exec(result.value)) !== null) {
      let rawTag = match[1].trim();

      rawTag = rawTag.replace(/^#/, "").replace(/^\//, "");

      tags.push(rawTag);
    }

    const uniqueTags = [...new Set(tags)];

    if (!uniqueTags.length) {
      return {
        isValid: false,
        message: `No tags found.`,
      };
    }

    const response = await dispatch(
      HandleApiActions({
        api_name: "/PCM/reports/template/validate",
        https_method: "POST",
        params: { tags: uniqueTags, scope_type: REPORT_SCOPE },
        show_toast: false,
      }),
    );

    if (!response?.data?.valid_tags?.length) {
      return {
        isValid: false,
        message: `Invalid tags: ${response.data.invalid_tags.join(", ")}`,
      };
    }

    return { isValid: true };
  };

  const handleUploadSuccess = async (uploadResponse) => {
    try {
      const { key, file } = uploadResponse;

      const name = templateName?.trim() || getTemplateNameFromFile(file);

      const response = await dispatch(
        HandleApiActions({
          api_name: "/PCM/reports/templates",
          https_method: "POST",
          params: {
            name,
            scope_type: REPORT_SCOPE,
            s3_key: key,
            file_name: file.name,
            file_size: file.size,
            content_type: file.type,
          },
          show_toast: true,
        }),
      );

      if (response?.error) return;

      setUploadOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Template creation failed", error);
    }
  };

  const breadcrumbs = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Report Templates" },
  ];

  const downloadFile = async (s3Key) => {
    try {
      const startResp = await dispatch(
        HandleApiActions({
          api_name: "/FSM/v1/assets/download/get/presigned-url",
          https_method: "GET",
          params: {
            key: s3Key,
          },
          show_toast: false,
        }),
      );
      console.log("startResp =", startResp);

      const presignedUrl = startResp?.data?.url;

      if (!presignedUrl) return;

      window.open(presignedUrl, "_blank");
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const config = {
    fetchAction: reportTemplateAction.get_templates,
    selector: (state) => state.reportTemplateReducer,
    onEdit: () => setUploadOpen(true),
    onView: (item) => downloadFile(item.s3_key),
  };

  return (
    <Box
      sx={{
        "& ::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "& ::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "& ::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
        },
        "& ::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
        scrollbarColor: "#888 #f1f1f1",
        scrollbarWidth: "thin",
      }}
    >
      <TemplateLayout
        breadcrumbs={breadcrumbs}
        addButtonConfig={{
          show: true,
          label: "",
          icon: "",
          onClick: () => setUploadOpen(true),
        }}
      >
        <TemplateManager config={config} refreshKey={refreshKey} />
      </TemplateLayout>

      {uploadOpen && (
        <FileUpload
          dialogMode
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          uploadUrl=""
          accept={[".docx"]}
          multiple={false}
          onUploadSuccess={(res) => {
            handleUploadSuccess(res);
          }}
          onUploadError={(err) => {
            console.log("error:", err);
          }}
          beforeUpload={validateReportTemplate}
        />

      )}
    </Box>
  );
}

export default ReportTemplatesPage;
